import { useTranslation } from '@/localization/useTranslation'
import { Button } from '@/components/Buttons/Button'
import { IPlaylistItem } from '@/types'
import { useImage } from '@/hooks/images/useImage'
import { ResponsivePictureImage } from '@/components/ResponsivePictureImage'
import { useEventDate } from '@/hooks/utils/useEventDate'
import { ITEM_KIND, getItemState } from '@/utils/playlists/getItemState'
import { BREAKPOINTS } from '@/constants'

import classNames from 'classnames'
import classes from './styles.module.scss'

const MOBILE_MEDIA = `(max-width: ${BREAKPOINTS.MOBILE - 1}px)`

interface TopBannerCardProps {
  item: IPlaylistItem
  itemIndex: number
  handleCardClick?: (item: any, action?: { type?: 'internal' | 'external', trigger?: string, link?: string }) => void
  isPlaceholder?: boolean
  shouldLoadImage?: boolean
}

export const TopBannerCard = (props: TopBannerCardProps) => {
  const {
    item,
    itemIndex,
    handleCardClick,
    isPlaceholder,
    shouldLoadImage = true
  } = props
  const { t } = useTranslation()
  const customButton = item?.customButton
  const { kind, event, isLive, hasVideos } = getItemState(item)
  const { date: eventDate } = useEventDate({
    event: event || undefined,
    format: 'human'
  })
  const isContent = kind === ITEM_KIND.CONTENT
  const isChannel = kind === ITEM_KIND.LIVE_CHANNEL
  // Only promise playback when the click will actually reach the player: the
  // navigation resolvers route on the item's videos, so live-without-videos
  // lands on the detail page and must not say "Watch Now".
  const canWatch = isLive && hasVideos
  const title = t(item?.title)?.trim()
  // Watchable items lead with the CTA alone. Everything else keeps a line of
  // copy: events swap the API's raw "Tournament - <ISO>" for a human date,
  // plain content keeps its configured description.
  const description = isContent
    ? t(item?.description?.plain)?.trim()
    : (canWatch ? '' : eventDate)
  // A channel with no videos has no destination at all (no event to fall back
  // to), so it gets no CTA rather than one that goes nowhere.
  const eventLabel = isChannel ? '' : t('See event')
  const buttonLabel = isContent
    ? t(customButton?.text)?.trim()
    : (canWatch ? t('WatchNow') : eventLabel)
  const buttonVariant = canWatch ? 'primary' : 'tertiary'
  const isPriority = itemIndex === 0
  const { url: background } = useImage({ images: item?.images, cut: 'panorama', scale: '100' })
  const { url: backgroundMobile } = useImage({ images: item?.images, cut: 'mediumv', scale: '100' })

  const handleClick = () => {
    // Channels and events have no configured action — let the navigation
    // resolvers pick the player (has videos) or the event detail page.
    if (kind !== ITEM_KIND.CONTENT) {
      handleCardClick?.(item)
      return
    }

    const { type, trigger, link } = customButton || {}

    handleCardClick?.(item, { type, trigger, link })
  }

  return (
    <div
      className={classNames({
        [classes.card]: true,
        [classes['card--placeholder']]: isPlaceholder
      })}
      onClick={handleClick}
    >
      {shouldLoadImage && (
        <ResponsivePictureImage
          className={classes.background}
          alt={title}
          fill
          priority={isPriority}
          loading={isPriority ? 'eager' : 'lazy'}
          fallbackSrc={background}
          fallbackSizes="100vw"
          sources={[
            { src: backgroundMobile, media: MOBILE_MEDIA, sizes: '100vw' },
          ]}
        />
      )}
      <div className={classes.content}>
        <div className={classes.content__info}>
          <div className={classes.content__text}>
            <>
              {itemIndex === 0 ?
                <h1 className={classes.title}>
                  {title}
                </h1> :
                <h2 className={classes.title}>
                  {title}
                </h2>
              }
            </>
            {description && (
              <h3 className={classes.description}>
                {description}
              </h3>
            )}
          </div>
          {buttonLabel &&
            <div className={classes.button}>
              <Button fullWidth onClick={handleClick} variant={buttonVariant}>
                {buttonLabel}
              </Button>
            </div>
          }
        </div>
      </div>
    </div>
  )
}
